/**
 * Every piece of text, and the data-driven lists, for the /about page
 * redesign (src/components/about/AboutPage.js). Components only render
 * this — no page copy is hardcoded in the component itself.
 *
 * Case study figures (STORY_TILES) were each checked against that client's
 * own case study page / data file before being copied here — see the
 * build report for the two tiles left out (no page exists yet for ORR
 * Protection or United Gulf Pipe Manufacturing).
 */

const HERO = {
  eyebrow: "ABOUT VISIOMATE",
  heading: "Business software, built and kept running by people you can reach.",
  body: "We are a Lahore-based team delivering Odoo, Dynamics 365, web and mobile software for clients in the US, UK, Europe and the Middle East.",
  primaryCta: { label: "Talk to us", to: "/contact" },
  secondaryCta: { label: "See our work", to: "/caseStudies" },
  // 2x2 grid: colourful office floor (left, spans both rows); two
  // colleagues working at a desk (top right); the "Our Customers, Our
  // Partners" wall (bottom right). The desks photo is the same one found
  // for /career's hero (good_environement1.webp) — a real Visiomate photo
  // of the team at their desks, just not named "desk" anything, which is
  // why earlier searches missed it.
  photoFloorFile: "lifeatvisiomate/vibrent_workplace.png",
  photoFloorAlt: "A colourful office floor at Visiomate, with a yellow and blue graphic wall",
  photoDeskFile: "lifeatvisiomate/good_environement1.webp",
  photoDeskAlt: "Colleagues working together at their desks at the Visiomate office",
  photoWallFile: "lifeatvisiomate/good_environement3.webp",
  photoWallAlt: "A wall inside the Visiomate office reading \"Our Customers, Our Partners\"",
}

const WORD_BAND = ["ODOO", "DYNAMICS 365", "WEB APPS", "MOBILE APPS", "INTEGRATIONS", "AI"]

const NUMBERS = [
  { value: "50+", label: "Clients served in total" },
  { value: "15+", label: "ERP clients, on Odoo and Dynamics 365" },
  { value: "95%", label: "Client retention rate", ring: 95 },
  // label only — the number itself is computed at render time from the
  // case study pages that actually exist (see AboutPage.js), never hardcoded
  { label: "Client stories you can read", computed: true },
]

const OUR_STORY = {
  eyebrow: "OUR STORY",
  heading: "A team that grew one project at a time.",
  body: "Our habit has never changed: do the work well, stay close to the client and let the next project come from the last one. The timeline shows how that habit shaped each year.",
  photoCaption: "The team behind the work",
  photoFile: "about/team-complete.jpg",
  photoAlt: "The complete Visiomate team together",
}

// Alternates left/right automatically by index — not stored per entry,
// since it's a layout rule, not a content fact.
const TIMELINE = [
  { year: "2017", title: "A new direction", text: "Our founder steps away from a software career to study for a PhD." },
  { year: "2018", title: "Visiomate begins", text: "One project with a business partner becomes a company.", dot: "yellow" },
  { year: "2019", title: "First international client", text: "A partnership that lasted two and a half years and ended on good terms." },
  { year: "2020", title: "Ten project milestones", text: "Ten project milestones reached with our clients." },
  { year: "2021", title: "Three continents", text: "We expanded to serve clients on three continents." },
  { year: "2022", title: "Three verticals", text: "Three multi-year international relationships kept going, across tailored web, tailored mobile and ERP implementation." },
  { year: "2023", title: "95% client retention", text: "A 95% client retention rate: our clients stay with us." },
  { year: "2024", title: "Official Odoo Partner", text: "We achieved official Odoo Partner status.", dot: "yellow" },
  { year: "2025", title: "A landmark project", text: "Orangeline, Lahore: our biggest project win to date." },
  { year: "2026", title: "Today", text: "Clients in manufacturing, retail, distribution, healthcare and more." },
]

const HOW_WE_WORK = {
  eyebrow: "HOW WE WORK",
  heading: "Four values we hold ourselves to.",
  panels: [
    {
      num: "01",
      label: "HAPPY CUSTOMER",
      text: "Every interaction is a chance to exceed expectations, not just meet them.",
      corner: "top-left",
      theme: "deep-navy",
    },
    {
      num: "02",
      label: "CUSTOMER AS PARTNER",
      text: "We work with clients, not for them, and treat their insights as the way to grow together.",
      corner: "top-right",
      theme: "pale-blue",
    },
    {
      num: "03",
      label: "EVERYONE SHOULD BE HEARD",
      text: "Every voice matters, and each one adds to our shared success.",
      corner: "bottom-left",
      theme: "yellow",
    },
    {
      num: "04",
      label: "CONTINUAL VALUE ADDITION",
      text: "We keep improving what we deliver, so the difference you see lasts.",
      corner: "bottom-right",
      theme: "near-black",
    },
  ],
}

const LEADERSHIP = {
  eyebrow: "OUR LEADERSHIP",
  heading: "The people you will deal with.",
  // name/title/linkedin/img are read from the existing LEADERS array in
  // src/pages/about.js (unchanged, per the brief) — only credentials/bio
  // are overridden here, keyed by name, and only where the brief changed them.
  overrides: {
    "Dr. Muhammad Tauseef Yunis Bhutti": {
      bio: "Software engineer turned delivery leader, with a PhD in Management Sciences. Mentor and trainer.",
    },
    "Hasan Arif": {
      credentials: "18 years in the software industry",
      bio: "Keeps our delivery, teams and clients running smoothly every day.",
    },
    "Rabi Ullah Khan": {
      bio: "Leads how we hire, support and look after our people.",
    },
    "Muhammad Zubair": {
      bio: "Leads how our technical teams plan, build and deliver client projects.",
    },
    "Muhammad Atique Yousaf": {
      bio: "Leads our ERP services, from implementation to day-to-day support.",
    },
  },
}

const STORIES_SECTION = {
  eyebrow: "PROOF, NOT PROMISES",
  heading: "Teams that run on Visiomate.",
  // The exact substring of `heading` that gets the yellow highlight
  // background (AboutPage.js splits heading on this string).
  highlight: "run on",
  detail: "A few client stories, each with a result you can check.",
  cta: { label: "See all case studies", to: "/caseStudies" },
}

// clientName must be an exact `name` from src/data/caseStudies.js's
// STORIES — AboutPage.js looks up that story's own `path` there and only
// renders a tile if allSitePage shows a page at that path. figure/figure2
// were each checked against that story's own case study page before being
// written here. `theme` is fixed per client (not cycled by position) —
// this order and the theme-per-client mapping are both from the brief.
const STORY_TILES = [
  {
    clientName: "Big Jerry's Fencing",
    tag: "Field Service · United States",
    figure: { value: "40%", label: "faster turnaround from lead to completed installation" },
    figure2: { value: "25%", label: "higher quote-to-win ratio" },
    visual: "none",
    theme: "deep-navy",
  },
  {
    clientName: "Net Atlantic",
    tag: "Software & SaaS",
    figure: { value: 35, label: "faster campaign deployment" },
    visual: "ring",
    theme: "yellow",
  },
  {
    clientName: "Orangeline Pakistan",
    tag: "Construction & Engineering · Lahore",
    figure: { value: 30, label: "faster project turnaround, from planning to final handover" },
    visual: "ring",
    theme: "near-black",
  },
  {
    clientName: "Nurtureful Psychology",
    tag: "Healthcare",
    figure: { value: 50, label: "less admin time on scheduling and patient onboarding" },
    visual: "ring",
    theme: "plain",
  },
  {
    clientName: "Muscat Media Group",
    tag: "Media · Oman",
    figure: { value: 100, label: "of HR workflow automated, off manual spreadsheets" },
    visual: "ring",
    theme: "slate",
  },
  {
    clientName: "EME Aquatic Systems",
    tag: "Construction & Engineering · Abu Dhabi",
    figure: { label: "3-stage procurement security, plus multi-currency finance" },
    visual: "stages",
    theme: "pale-blue",
  },
  // ORR Protection and United Gulf Pipe Manufacturing have no case study
  // page yet (no file under src/pages/caseStudies/projects/) and no stated
  // percentage anywhere in the repo (only a one-line `built:` string in
  // src/data/caseStudies.js, no metrics) — so there's no figure to show,
  // per "never show an unverified figure". `showWithoutPage` renders them
  // as non-link blocks in the meantime (see AboutPage.js); once a real
  // page exists at their STORIES path, they become links automatically,
  // no code change needed.
  {
    clientName: "ORR Protection",
    figure: { label: "technician portal and planner for a fire protection specialist" },
    visual: "bars",
    theme: "near-black",
    showWithoutPage: true,
  },
  {
    clientName: "United Gulf Pipe Manufacturing",
    figure: { label: "CRM, sales, purchase and manufacturing for a maker of HDPE pipes" },
    visual: "rings4",
    theme: "plain",
    showWithoutPage: true,
  },
]

const PARTNER = {
  badgeFile: "odoo_ready_partners.png",
  heading: "Visiomate is an Odoo Ready Partner.",
  body: "Odoo recognises us as a partner it can rely on to set up and support its business software.",
}

const WHERE_WE_WORK = {
  eyebrow: "WHERE WE WORK",
  heading: "Based in Lahore, working your hours.",
  body: "Tell us your time zone and we will show you when our team is online. Support hours are listed per service, so you know what to expect.",
  regions: [
    { name: "United States", overlap: "4 to 8 hours of overlap", theme: "deep-navy" },
    { name: "United Kingdom", overlap: "4 to 8 hours of overlap", theme: "near-black" },
    { name: "Europe", overlap: "4 to 8 hours of overlap", theme: "slate" },
    { name: "Middle East", overlap: "100% overlap", theme: "yellow" },
  ],
}

const CLOSING = {
  // "two lines" in the brief is a width/wrap instruction for the section-
  // heading style (AboutPage.js caps the heading's width so it wraps),
  // not a second sentence — the text itself is this one line.
  heading: "Have a project in mind?",
  body: "Tell us what is slowing your business down. We will reply with a plan.",
  cta: { label: "Let's talk business", to: "/contact" },
}

module.exports = {
  HERO,
  WORD_BAND,
  NUMBERS,
  OUR_STORY,
  TIMELINE,
  HOW_WE_WORK,
  LEADERSHIP,
  STORIES_SECTION,
  STORY_TILES,
  PARTNER,
  WHERE_WE_WORK,
  CLOSING,
}
